/*
 * THE KITE: one kite on a string, drifting behind the page boxes ("Behind the boxes" > Kite).
 *
 * A port of design-prototypes/empty-state-motion/src/critters/kite.js, the kite Chris picked from the idle
 * critters sheet ("I like the owl, the fish bowl, the kite, and the plant"). Same path, same panels, same bows,
 * same colours; only the panel size is no longer a fixed 788 x 309 (it follows the real grey panel) and the
 * visitor "gust" is gone, because this layer is only ever shown while the site is quiet: a visitor arriving ends it
 * (ambientLife.ts fades the whole ghost layer out), so there is nobody to gust for.
 *
 * Everything is an ANALYTIC function of time. The tail is simply where the kite was a moment ago (each bow samples
 * the same path a little earlier), which gives a believable trailing curve with no state to keep, and means the
 * service needs no per-frame stepping for it: draw(t) is all there is.
 *
 * A lull every 24 seconds lets the kite sag and dip, and the string go slack.
 */

/** the panel the mock was drawn for; everything is scaled from it */
export const KITE_MOCK_W = 788;
export const KITE_MOCK_H = 309;
const PANEL_COLOURS = ["#dd3f4e", "#4fb2b7", "#f6ba22", "#845e9f"];

const clamp = (x: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const ease = (k: number) => { const c = clamp(k); return c * c * (3 - 2 * c); };

/* ───────── colour: the "soft vinyl toy" kit (light top left, shade pushed toward violet, no outlines) ───────── */
const parse = (c: string): number[] => {
  if (c.charAt(0) === "#") {
    const h = c.length === 4 ? `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}` : c;
    return [parseInt(h.substr(1, 2), 16), parseInt(h.substr(3, 2), 16), parseInt(h.substr(5, 2), 16)];
  }
  const m = /rgba?\(([^)]+)\)/.exec(c);
  const p = (m ? m[1] : "0,0,0").split(",");
  return [+p[0], +p[1], +p[2]];
};
const mix = (a: string, b: string, t: number) => {
  const A = parse(a), B = parse(b);
  return `rgb(${Math.round(lerp(A[0], B[0], t))},${Math.round(lerp(A[1], B[1], t))},${Math.round(lerp(A[2], B[2], t))})`;
};
const rgba = (c: string, a: number) => { const p = parse(c); return `rgba(${p[0]},${p[1]},${p[2]},${a})`; };
const light = (c: string, t = 0.42) => mix(c, "#ffffff", t);
const shade = (c: string, t = 0.34) => mix(c, "#3a2766", t);

/** a soft white highlight */
function gloss(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number, rot: number, a: number) {
  ctx.save(); ctx.translate(cx, cy); ctx.rotate(rot); ctx.scale(1, ry / rx);
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
  g.addColorStop(0, `rgba(255,255,255,${a})`); g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, rx, 0, 6.2832); ctx.fill(); ctx.restore();
}

/** a lit sphere: highlight up and left, violet shade down and right, a warm bounce on the shade side */
function sphere(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, base: string) {
  ctx.save(); ctx.translate(cx, cy);
  const g = ctx.createRadialGradient(-0.34 * r, -0.42 * r, 0.04 * r, -0.06 * r, -0.08 * r, 1.12 * r);
  g.addColorStop(0, light(base, 0.5)); g.addColorStop(0.32, light(base, 0.14)); g.addColorStop(0.7, base); g.addColorStop(1, shade(base, 0.38));
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 6.2832); ctx.fillStyle = g; ctx.fill();
  ctx.save(); ctx.clip();
  const b = ctx.createRadialGradient(0.34 * r, 0.9 * r, 0, 0.34 * r, 0.9 * r, 0.9 * r);
  b.addColorStop(0, rgba(mix(base, "#ffd2a0", 0.55), 0.5)); b.addColorStop(1, rgba(base, 0));
  ctx.fillStyle = b; ctx.fillRect(-r, -r, 2 * r, 2 * r); ctx.restore();
  ctx.restore();
  gloss(ctx, cx - 0.32 * r, cy - 0.4 * r, 0.42 * r, 0.3 * r, -0.5, 0.85);
}

/** a bow of the tail: a leaf-shaped piece of cloth, drawn along +x from the origin */
function leaf(ctx: CanvasRenderingContext2D, len: number, wid: number, base: string) {
  const g = ctx.createLinearGradient(0, -wid, len, wid * 0.6);
  g.addColorStop(0, shade(base, 0.2)); g.addColorStop(0.45, base); g.addColorStop(1, light(base, 0.34));
  ctx.beginPath(); ctx.moveTo(0, 0);
  ctx.bezierCurveTo(len * 0.22, -wid * 1.05, len * 0.72, -wid * 0.95, len, 0);
  ctx.bezierCurveTo(len * 0.72, wid * 0.78, len * 0.24, wid * 0.88, 0, 0);
  ctx.closePath(); ctx.fillStyle = g; ctx.fill();
  ctx.save(); ctx.clip();
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.quadraticCurveTo(len * 0.5, -wid * 0.05, len * 0.94, 0);
  ctx.lineWidth = Math.max(0.5, wid * 0.09); ctx.strokeStyle = rgba(light(base, 0.5), 0.65); ctx.lineCap = "round"; ctx.stroke();
  gloss(ctx, len * 0.38, -wid * 0.45, len * 0.28, wid * 0.3, -0.2, 0.28);
  ctx.restore();
}

/* ───────── the path ───────── */
/** 1 at the bottom of a lull (every 24s, centred on t=14), 0 outside it */
export const kiteLull = (t: number) => ease(clamp(1 - Math.abs((t % 24) - 14) / 3.4));

/** where the kite is at time t, in panel units; u is the size scale (1 at the mock's panel) */
export function kitePath(t: number, W: number, H: number, u: number) {
  const l = kiteLull(t);
  return {
    x: W * 0.5 + Math.sin(t * 0.17) * W * 0.34 + Math.sin(t * 0.43 + 1) * 28 * u,
    y: H * 0.35 + Math.sin(t * 0.52) * 14 * u + Math.sin(t * 1.1) * 3 * u + l * 96 * u,
    l,
  };
}

/** the size scale for a panel of W x H user units */
export const kiteScale = (W: number, H: number) => clamp(Math.min(W / KITE_MOCK_W, H / KITE_MOCK_H), 0.6, 1.5);

/**
 * Draws the kite into a panel of W x H user units (the context is already in them).
 * `alpha` is the layer's own fade (0 to 1); the mock drew the kite at 0.92 of it.
 */
export function drawKite(ctx: CanvasRenderingContext2D, W: number, H: number, t: number, alpha: number) {
  const a = clamp(alpha);
  if (a <= 0.002 || W <= 0 || H <= 0) return;
  const u = kiteScale(W, H);
  const P = kitePath(t, W, H, u), P2 = kitePath(t + 0.06, W, H, u);
  const vx = (P2.x - P.x) / 0.06;
  const ang = clamp(vx / (160 * (W / KITE_MOCK_W)), -0.42, 0.42) + Math.sin(t * 1.3) * 0.05;
  const SZ = 1.32 * u;
  const anchor = [W * 0.5 + Math.sin(t * 0.17) * W * 0.12 - 40 * u, H + 26 * u];
  const rot = (px: number, py: number) => {
    const x = px * SZ, y = py * SZ, c = Math.cos(ang), s = Math.sin(ang);
    return [P.x + x * c - y * s, P.y + x * s + y * c];
  };
  const bridle = rot(0, 6), tailStart = rot(0, 36);

  ctx.save();
  ctx.globalAlpha = a;

  // the string: slack in a lull, taut otherwise
  const sag = (0.25 + P.l * 0.75) * 54 * u;
  const cx = lerp(anchor[0], bridle[0], 0.5) + Math.sin(t * 0.8) * 5 * u - 16 * u;
  const cy = lerp(anchor[1], bridle[1], 0.55) + sag;
  ctx.beginPath(); ctx.moveTo(anchor[0], anchor[1]); ctx.quadraticCurveTo(cx, cy, bridle[0], bridle[1]);
  ctx.lineWidth = 1.5; ctx.strokeStyle = "rgba(56,66,84,0.18)"; ctx.stroke();
  ctx.lineWidth = 0.8; ctx.strokeStyle = "rgba(56,66,84,0.62)"; ctx.stroke();

  // the tail: the kite's own path a moment ago, pulled down by weight, with bows strung along it
  const tp: number[][] = [tailStart];
  const bows: Array<{ p: number[]; c: string }> = [];
  for (let i = 1; i <= 9; i++) {
    const pp = kitePath(t - i * 0.13, W, H, u);
    const drop = i * 10.4 * u;
    const lagX = (pp.x - P.x) * 0.85, lagY = (pp.y - P.y) * 0.85;
    tp.push([tailStart[0] + lagX + Math.sin(t * 2 + i * 0.9) * 2.4 * i * 0.3 * u, tailStart[1] + lagY + drop]);
    if (i % 2 === 0) bows.push({ p: tp[i], c: PANEL_COLOURS[(i / 2) % 4] });
  }
  ctx.beginPath(); ctx.moveTo(tp[0][0], tp[0][1]);
  for (let q = 1; q < tp.length; q++) ctx.lineTo(tp[q][0], tp[q][1]);
  ctx.lineWidth = 1; ctx.strokeStyle = "rgba(56,66,84,0.6)"; ctx.stroke();
  bows.forEach((b, bi) => {
    ctx.save(); ctx.translate(b.p[0], b.p[1]); ctx.scale(u, u); ctx.rotate(Math.sin(t * 2.4 + bi) * 0.5 + 0.2);
    [-1, 1].forEach((sd) => { ctx.save(); ctx.rotate(sd * 0.7); leaf(ctx, 12.5, 5.4, b.c); ctx.restore(); });
    sphere(ctx, 0, 0, 2.3, mix(b.c, "#ffffff", 0.2));
    ctx.restore();
  });

  // the kite: four lit panels, two spars
  ctx.save(); ctx.translate(P.x, P.y); ctx.rotate(ang); ctx.scale(SZ, SZ);
  const T = [0, -35], L = [-24, 0], R = [24, 0], B = [0, 36], M = [0, 0];
  const panel = (p1: number[], p2: number[], p3: number[], col: string, bulge: number[]) => {
    const g2 = ctx.createLinearGradient(p1[0], p1[1], (p2[0] + p3[0]) / 2, (p2[1] + p3[1]) / 2);
    g2.addColorStop(0, light(col, 0.34)); g2.addColorStop(0.5, col); g2.addColorStop(1, shade(col, 0.34));
    ctx.beginPath(); ctx.moveTo(p1[0], p1[1]);
    ctx.quadraticCurveTo((p1[0] + p2[0]) / 2 + bulge[0], (p1[1] + p2[1]) / 2 + bulge[1], p2[0], p2[1]);
    ctx.lineTo(p3[0], p3[1]);
    ctx.quadraticCurveTo((p1[0] + p3[0]) / 2 - bulge[0], (p1[1] + p3[1]) / 2 - bulge[1], p1[0], p1[1]);
    ctx.closePath(); ctx.fillStyle = g2; ctx.fill();
  };
  panel(T, L, M, PANEL_COLOURS[0], [-1.4, -1.0]); panel(T, R, M, PANEL_COLOURS[1], [1.4, -1.0]);
  panel(B, L, M, PANEL_COLOURS[2], [-1.8, 1.4]); panel(B, R, M, PANEL_COLOURS[3], [1.8, 1.4]);
  ctx.lineCap = "round"; ctx.strokeStyle = "rgba(244,232,205,0.95)"; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(T[0], T[1]); ctx.lineTo(B[0], B[1]); ctx.moveTo(L[0], L[1]); ctx.lineTo(R[0], R[1]); ctx.stroke();
  ctx.strokeStyle = "rgba(150,120,80,0.5)"; ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.moveTo(T[0] + 0.6, T[1]); ctx.lineTo(B[0] + 0.6, B[1]); ctx.moveTo(L[0], L[1] + 0.6); ctx.lineTo(R[0], R[1] + 0.6); ctx.stroke();
  gloss(ctx, -9, -13, 9, 6, -0.5, 0.35);
  sphere(ctx, 0, 0, 1.8, "#f1e3c0");
  ctx.restore();

  ctx.restore();
}
